import React, { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { apiUrl } from '../../constants';
import { brand } from '../../brand/brand';
import { logo } from '../../brand/assets';
import LoaderInline from '../../components/Loader/LoaderInline';
import { CLIENT_UPLOAD_KINDS, shrinkImage } from './clientUploadKinds';

// Публичная страница без логина: клиент по ссылке/QR от менеджера присылает
// выписку и фото. Только голый axios — токен авторизации здесь не нужен.
const base = (token) => `${apiUrl}/api/public/clientUpload/${token}`;

const errorText = (e) =>
  e?.response?.data?.errorMessage || 'Не удалось отправить файл. Проверьте интернет и попробуйте ещё раз.';

const Pick = ({ children, capture, accept, disabled, onFile, primary, small }) => (
  <label
    style={{
      ...styles.button,
      ...(primary ? styles.primary : styles.secondary),
      ...(small ? styles.small : {}),
      opacity: disabled ? 0.5 : 1,
    }}
  >
    {children}
    <input
      type="file"
      accept={accept}
      capture={capture}
      disabled={disabled}
      style={{ display: 'none' }}
      onChange={(e) => {
        onFile(e.target.files?.[0]);
        e.target.value = '';
      }}
    />
  </label>
);

const Card = ({ number, kind, done, preview, busy, progress, error, locked, onFile }) => {
  const meta = CLIENT_UPLOAD_KINDS[kind];
  const accept = meta.photo ? 'image/*' : 'application/pdf,image/*';
  return (
    <div style={{ ...styles.card, ...(done ? styles.cardDone : {}) }}>
      <div style={styles.cardHead}>
        <span style={{ ...styles.badge, ...(done ? styles.badgeDone : {}) }}>{done ? '✓' : number}</span>
        <div>
          <b>{meta.title}</b>
          <div style={styles.hint}>{meta.hint}</div>
        </div>
      </div>

      {done && preview?.url && <img src={preview.url} alt="" style={styles.preview} />}

      {busy ? (
        <div>
          <div style={styles.barBack}>
            <div style={{ ...styles.barFill, width: `${progress}%` }} />
          </div>
          <div style={styles.hint}>Отправляем… {progress}%</div>
        </div>
      ) : done ? (
        <div style={styles.doneRow}>
          <span style={styles.doneText}>Отправлено менеджеру</span>
          <Pick small accept={accept} disabled={locked} onFile={onFile}>
            Заменить
          </Pick>
        </div>
      ) : (
        <div style={styles.actions}>
          {meta.photo ? (
            <>
              <Pick primary capture="user" accept={accept} disabled={locked} onFile={onFile}>
                Сфотографироваться
              </Pick>
              <Pick accept={accept} disabled={locked} onFile={onFile}>
                Выбрать из галереи
              </Pick>
            </>
          ) : (
            <>
              <Pick primary capture="environment" accept="image/*" disabled={locked} onFile={onFile}>
                Сфотографировать документ
              </Pick>
              {/* Только PDF: со смешанным accept многие Android-телефоны открывают лишь выбор фото, без «Файлов». */}
              <Pick accept="application/pdf,.pdf" disabled={locked} onFile={onFile}>
                Выбрать PDF-файл
              </Pick>
              <Pick small accept="image/*" disabled={locked} onFile={onFile}>
                Фото из галереи
              </Pick>
            </>
          )}
        </div>
      )}
      {error && <div style={styles.error}>{error}</div>}
    </div>
  );
};

const ClientUpload = () => {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [fatal, setFatal] = useState(null);
  const [uploaded, setUploaded] = useState([]);
  const [previews, setPreviews] = useState({});
  const [busy, setBusy] = useState(null);
  const [progress, setProgress] = useState(0);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    // Токен в адресе: не отдаём его в Referer и не пускаем страницу в поисковики.
    const metas = [
      ['referrer', 'no-referrer'],
      ['robots', 'noindex, nofollow'],
    ].map(([name, content]) => {
      const m = document.createElement('meta');
      m.name = name;
      m.content = content;
      document.head.appendChild(m);
      return m;
    });
    return () => metas.forEach((m) => m.remove());
  }, []);

  useEffect(() => {
    axios
      .get(base(token))
      .then((res) => {
        setInfo(res.data);
        setUploaded(res.data.uploaded || []);
      })
      .catch((e) => setFatal(e?.response?.data?.errorMessage || 'Ссылка не найдена. Попросите менеджера прислать новую.'));
  }, [token]);

  const send = useCallback(
    async (kind, picked) => {
      if (!picked) return;
      setBusy(kind);
      setProgress(0);
      setErrors((prev) => ({ ...prev, [kind]: null }));
      try {
        const file = await shrinkImage(picked);
        const form = new FormData();
        form.append('file', file);
        await axios.post(`${base(token)}/${kind}`, form, {
          onUploadProgress: (ev) => ev.total && setProgress(Math.round((ev.loaded * 100) / ev.total)),
        });
        setUploaded((prev) => (prev.includes(kind) ? prev : [...prev, kind]));
        setPreviews((prev) => ({
          ...prev,
          [kind]: file.type.startsWith('image/') ? { url: URL.createObjectURL(file) } : null,
        }));
      } catch (e) {
        setErrors((prev) => ({ ...prev, [kind]: errorText(e) }));
      } finally {
        setBusy(null);
      }
    },
    [token]
  );

  const header = (
    <div style={styles.header}>
      <img src={logo} alt={brand.title} style={{ height: 28 }} />
    </div>
  );

  if (fatal) {
    return (
      <div style={styles.root}>
        {header}
        <div style={styles.page}>
          <h2 style={styles.h2}>Ссылка не работает</h2>
          <p>{fatal}</p>
        </div>
      </div>
    );
  }
  if (!info) {
    return (
      <div style={styles.root}>
        {header}
        <div style={{ ...styles.page, textAlign: 'center' }}>
          <LoaderInline />
        </div>
      </div>
    );
  }

  const total = info.kinds.length;
  const sent = info.kinds.filter((k) => uploaded.includes(k)).length;

  return (
    <div style={styles.root}>
      {header}
      <div style={styles.page}>
        <h2 style={styles.h2}>Документы для заявки</h2>
        <div style={styles.counter}>
          Отправлено: {sent} из {total}
        </div>

        {info.kinds.map((kind, i) =>
          CLIENT_UPLOAD_KINDS[kind] ? (
            <Card
              key={kind}
              number={i + 1}
              kind={kind}
              done={uploaded.includes(kind)}
              preview={previews[kind]}
              busy={busy === kind}
              progress={progress}
              error={errors[kind]}
              locked={busy !== null}
              onFile={(f) => send(kind, f)}
            />
          ) : null
        )}

        {sent === total && (
          <div style={styles.finish}>
            <b>Готово!</b> Все документы загружены. Эту страницу можно закрыть.
          </div>
        )}
      </div>
    </div>
  );
};

const styles = {
  root: { minHeight: '100vh', background: '#f6f6f6', fontFamily: 'system-ui, sans-serif', color: '#222' },
  header: { background: 'var(--brand-dark, #494136)', padding: '14px 20px' },
  page: { maxWidth: 480, margin: '0 auto', padding: '20px 16px 40px', display: 'grid', gap: 14 },
  h2: { margin: 0, fontSize: 22 },
  counter: { fontWeight: 600 },
  card: { background: '#fff', border: '1px solid #e2e2e2', borderRadius: 12, padding: 16, display: 'grid', gap: 12 },
  cardDone: { borderColor: '#1a8f3c' },
  cardHead: { display: 'flex', gap: 12, alignItems: 'center' },
  badge: {
    width: 32,
    height: 32,
    borderRadius: '50%',
    background: 'var(--brand-dark, #494136)',
    color: '#fff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    fontWeight: 600,
  },
  badgeDone: { background: '#1a8f3c' },
  hint: { color: '#777', fontSize: 14 },
  preview: { maxWidth: '100%', maxHeight: 220, objectFit: 'contain', borderRadius: 8, justifySelf: 'center' },
  actions: { display: 'grid', gap: 8 },
  button: { borderRadius: 10, padding: '14px 16px', textAlign: 'center', cursor: 'pointer', fontSize: 16, fontWeight: 600 },
  primary: { background: 'var(--brand-dark, #494136)', color: '#fff' },
  secondary: { background: '#fff', color: 'var(--brand-dark, #494136)', border: '1px solid var(--brand-dark, #494136)' },
  barBack: { height: 8, background: '#e8e8e8', borderRadius: 4, overflow: 'hidden', marginBottom: 6 },
  barFill: { height: '100%', background: '#1a8f3c', transition: 'width .2s' },
  small: { padding: '8px 16px', fontSize: 14 },
  doneRow: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 },
  doneText: { color: '#1a8f3c', fontSize: 14, fontWeight: 600 },
  error: { color: '#c0392b', fontSize: 14 },
  finish: { background: '#e6f4ea', borderRadius: 12, padding: 16 },
};

export default ClientUpload;
